import { useEffect, useRef, useState } from 'react';
import { loadGoogleMaps, getGoogleMapsMapId, getMapColorScheme } from '../../../utils/googleMapsLoader';
import type { Property } from '../../../mocks/properties';
import { regionBoundsPoints, type DrawnRegion, type LatLng } from '../../../utils/mapRegion';

interface RegionMiniMapProps {
  /** The committed region drawn on the main search map (or a district outline). */
  region: DrawnRegion;
  /** Properties already filtered to the region — plotted as small dots. */
  properties: Property[];
  /** When there is no region shape to draw (e.g. a city search), frame these
   *  points — typically the city's bounding-box corners — so the whole city fits
   *  the window, without drawing any outline. Ignored when `region` is set. */
  frameBounds?: LatLng[];
}

// Cap the dots so a dense region doesn't spawn hundreds of overlays on what is
// only ever a small preview map.
const MAX_DOTS = 60;
const DEFAULT_CENTER = { lat: 21.4858, lng: 39.1925 };

/**
 * A small, non-interactive Google map that frames the region the user drew on
 * the main search map. It exists so that, in grid view (where the big map is
 * hidden), the user can still see that the listed results are limited to a
 * specific area. Purely read-only: gestures are disabled and it owns no drawing
 * state — it just renders whatever region the parent hands it.
 */
export default function RegionMiniMap({ region, properties, frameBounds }: RegionMiniMapProps) {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const overlaysRef = useRef<Array<{ setMap: (map: google.maps.Map | null) => void }>>([]);
  // AdvancedMarkerElement has no setMap(), so the property dots are tracked
  // separately and torn down via `.map = null`.
  const dotMarkersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const [isReady, setIsReady] = useState(false);
  const [failed, setFailed] = useState(false);

  // Initialise the map once.
  useEffect(() => {
    let isMounted = true;

    loadGoogleMaps()
      .then(() => {
        if (!isMounted || !mapRef.current) return;
        // The app's slim Maps typings (vite-env.d.ts) list only a few MapOptions
        // keys; the interactivity-disabling keys are real at runtime, so widen
        // the option object to carry them past the type checker.
        const options: google.maps.MapOptions & Record<string, unknown> = {
          center: DEFAULT_CENTER,
          zoom: 11,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: false,
          disableDefaultUI: true,
          gestureHandling: 'none',
          keyboardShortcuts: false,
          clickableIcons: false,
          // Required for AdvancedMarkerElement; inline `styles` is ignored with a
          // mapId, so POI-label hiding moves to cloud-based map styles.
          mapId: getGoogleMapsMapId(),
          colorScheme: getMapColorScheme(),
        };
        const map = new google.maps.Map(mapRef.current, options);
        mapInstanceRef.current = map;
        setIsReady(true);
      })
      .catch(() => {
        if (isMounted) setFailed(true);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // (Re)draw the region shape + property dots and frame them whenever the
  // region or its property set changes.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !isReady || (!region && !(frameBounds && frameBounds.length))) return;

    overlaysRef.current.forEach((overlay) => overlay.setMap(null));
    overlaysRef.current = [];
    dotMarkersRef.current.forEach((m) => { m.map = null; });
    dotMarkersRef.current = [];

    const bounds = new google.maps.LatLngBounds();
    let hasBounds = false;
    const extend = (point: { lat: number; lng: number }) => {
      bounds.extend(point);
      hasBounds = true;
    };

    if (region) {
      if (region.kind === 'radius') {
        const circle = new google.maps.Circle({
          map,
          center: region.center,
          radius: region.radiusMeters,
          strokeColor: '#004D40',
          strokeOpacity: 0.9,
          strokeWeight: 2,
          fillColor: '#004D40',
          fillOpacity: 0.12,
          clickable: false,
        });
        overlaysRef.current.push(circle);
      } else {
        const polygon = new google.maps.Polygon({
          map,
          paths: region.path,
          strokeColor: '#004D40',
          strokeOpacity: 0.9,
          strokeWeight: 2,
          fillColor: '#004D40',
          fillOpacity: 0.12,
          clickable: false,
        });
        overlaysRef.current.push(polygon);
      }
    }

    // Frame the region shape (polygon vertices / circle bbox), or — for a city
    // search with no outline — the supplied bounds so the whole city fits.
    (region ? regionBoundsPoints(region) : frameBounds ?? []).forEach(extend);

    properties.slice(0, MAX_DOTS).forEach((property) => {
      if (property.latitude == null || property.longitude == null) return;
      const position = { lat: property.latitude, lng: property.longitude };
      // DOM dot (SymbolPath CIRCLE scale 5 = radius 5 → a 10px circle), wrapped so
      // its center sits on the point (AdvancedMarker anchors bottom-center).
      const dot = document.createElement('div');
      dot.style.cssText =
        'width:10px;height:10px;border-radius:9999px;background:#003930;' +
        'border:1.5px solid #ffffff;box-sizing:border-box;';
      const dotWrap = document.createElement('div');
      dotWrap.style.transform = 'translateY(50%)';
      dotWrap.appendChild(dot);
      const marker = new google.maps.marker.AdvancedMarkerElement({
        map,
        position,
        content: dotWrap,
        gmpClickable: false,
        zIndex: 1,
      });
      dotMarkersRef.current.push(marker);
      // NB: do NOT extend the frame by dots — the frame is defined solely by the
      // region/frameBounds so a stray/off-area listing can't zoom the map out.
    });

    if (hasBounds) {
      map.fitBounds(bounds, 24);
    }
    // Safety net in case the container measured 0×0 at first paint.
    google.maps.event.trigger(map, 'resize');
  }, [isReady, region, properties, frameBounds]);

  // Tear down overlays on unmount.
  useEffect(
    () => () => {
      overlaysRef.current.forEach((overlay) => overlay.setMap(null));
      overlaysRef.current = [];
      dotMarkersRef.current.forEach((m) => { m.map = null; });
      dotMarkersRef.current = [];
    },
    [],
  );

  if (failed) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-slate-100 text-slate-400">
        <i className="ri-map-2-line text-3xl" aria-hidden="true"></i>
      </div>
    );
  }

  return (
    <div className="relative h-full w-full bg-slate-100">
      <div ref={mapRef} className="absolute inset-0" />
    </div>
  );
}
